import { useCallback, useContext, useEffect, useId, useImperativeHandle, useMemo, useRef, useState, useSyncExternalStore, } from 'react';
import { MediaDropLabelsContext, resolveLabels } from './labels.js';
import { filesFromDrop } from './entries.js';
import { createUploadSession } from './session.js';
function fileCount(e) {
    const transfer = e.dataTransfer;
    if (!transfer)
        return 0;
    const count = Array.from(transfer.items ?? []).filter((item) => item.kind === 'file').length;
    return count || (transfer.types.includes('Files') ? 1 : 0);
}
function modeOf(count) {
    if (!count)
        return 'empty';
    if (count === 1)
        return 'tile';
    return 'queue';
}
function liveTextOf(labels, items, doneCount) {
    if (!items.length)
        return '';
    if (doneCount === items.length)
        return labels.added(items.length);
    return labels.uploading(doneCount, items.length);
}
export { kindOf } from './session.js';
export function useMediaDrop(props) {
    const uid = useId();
    const [ownSession] = useState(() => props.session ?? createUploadSession(props));
    const disposalTimer = useRef(null);
    const session = props.session ?? ownSession;
    const items = useSyncExternalStore(session.subscribe, session.getSnapshot, session.getSnapshot);
    const [drag, setDrag] = useState({ over: false, count: 0 });
    const [notice, setNotice] = useState(null);
    const [shake, setShake] = useState(0);
    const labels = resolveLabels(useContext(MediaDropLabelsContext), props.labels);
    const propsRef = useRef(props);
    propsRef.current = props;
    const dragDepth = useRef(0);
    const dragRef = useRef(drag);
    dragRef.current = drag;
    const inputRef = useRef(null);
    useEffect(() => {
        session.configure({
            ...props,
            onRejected(rejections) {
                props.onRejected?.(rejections);
                setNotice({ id: Date.now(), text: labels.rejected(rejections[0], rejections.length) });
                setShake((n) => n + 1);
            },
        });
    });
    const seeded = useRef(false);
    useEffect(() => {
        if (seeded.current)
            return;
        seeded.current = true;
        if (props.initialFiles?.length)
            void session.addFiles(props.initialFiles);
    }, [props.initialFiles, session]);
    const seenExisting = useRef(new Set());
    useEffect(() => {
        const additions = (props.existing ?? []).filter((entry) => {
            if (seenExisting.current.has(entry.id))
                return false;
            seenExisting.current.add(entry.id);
            return !session.getSnapshot().some((item) => item.result === entry.url);
        });
        if (additions.length)
            session.addExisting(additions);
    }, [props.existing, session]);
    useEffect(() => {
        if (!notice)
            return;
        const timer = setTimeout(() => setNotice(null), 3200);
        return () => clearTimeout(timer);
    }, [notice]);
    useEffect(() => {
        if (props.session)
            return;
        if (disposalTimer.current)
            clearTimeout(disposalTimer.current);
        return () => {
            // StrictMode replays effect cleanup before its second mount. Defer
            // disposal one tick so the replay can keep the same upload session.
            disposalTimer.current = setTimeout(() => ownSession.dispose(), 0);
        };
    }, [ownSession, props.session]);
    const addFiles = useCallback((files) => {
        void session.addFiles(files);
    }, [session]);
    const openPicker = useCallback(() => {
        if (propsRef.current.disabled)
            return;
        propsRef.current.onBeforeOpenPicker?.();
        inputRef.current?.click();
    }, []);
    // The control's own click/Enter. `onBrowse` swaps in an app picker here only;
    // the imperative handle's `openPicker` always opens the native dialog.
    const browse = useCallback(() => {
        const { disabled, onBrowse } = propsRef.current;
        if (disabled)
            return;
        if (!onBrowse) {
            openPicker();
            return;
        }
        onBrowse();
    }, [openPicker]);
    useImperativeHandle(props.ref, () => ({
        addFiles,
        openPicker,
        start: session.start,
        clear: session.clear,
        items: session.getSnapshot,
    }), [addFiles, openPicker, session]);
    // Only file drags are ours: text drags keep their default, and a file drag on a
    // disabled zone is still prevented so the browser does not navigate to the file.
    const dragEnter = useCallback((e) => {
        const count = fileCount(e);
        if (!count)
            return;
        e.preventDefault();
        if (propsRef.current.disabled) {
            e.dataTransfer.dropEffect = 'none';
            return;
        }
        dragDepth.current++;
        if (!dragRef.current.over) {
            setDrag({ over: true, count });
            propsRef.current.onDragStateChange?.(true, count);
        }
    }, []);
    const dragOver = useCallback((e) => {
        if (!fileCount(e))
            return;
        e.preventDefault();
        e.dataTransfer.dropEffect = propsRef.current.disabled ? 'none' : 'copy';
    }, []);
    const dragLeave = useCallback((e) => {
        e.preventDefault();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0 && dragRef.current.over) {
            setDrag({ over: false, count: 0 });
            propsRef.current.onDragStateChange?.(false, 0);
        }
    }, []);
    const drop = useCallback((e) => {
        if (!fileCount(e))
            return;
        e.preventDefault();
        dragDepth.current = 0;
        if (dragRef.current.over) {
            setDrag({ over: false, count: 0 });
            propsRef.current.onDragStateChange?.(false, 0);
        }
        if (propsRef.current.disabled)
            return;
        void filesFromDrop(e.dataTransfer).then(addFiles);
    }, [addFiles]);
    const [scopeEl, setScopeEl] = useState(null);
    useEffect(() => {
        setScopeEl(props.dropScope?.current ?? null);
    }, [props.dropScope]);
    useEffect(() => {
        if (!scopeEl)
            return;
        scopeEl.addEventListener('dragenter', dragEnter);
        scopeEl.addEventListener('dragover', dragOver);
        scopeEl.addEventListener('dragleave', dragLeave);
        scopeEl.addEventListener('drop', drop);
        return () => {
            scopeEl.removeEventListener('dragenter', dragEnter);
            scopeEl.removeEventListener('dragover', dragOver);
            scopeEl.removeEventListener('dragleave', dragLeave);
            scopeEl.removeEventListener('drop', drop);
        };
    }, [scopeEl, dragEnter, dragOver, dragLeave, drop]);
    useEffect(() => {
        const target = props.pasteScope?.current;
        if (!target)
            return;
        const onPaste = (event) => {
            if (propsRef.current.disabled)
                return;
            const files = Array.from(event.clipboardData?.files ?? []);
            if (files.length) {
                event.preventDefault();
                addFiles(files);
            }
        };
        target.addEventListener('paste', onPaste);
        return () => target.removeEventListener('paste', onPaste);
    }, [addFiles, props.pasteScope]);
    const onKeyDown = useCallback((e) => {
        if (e.target !== e.currentTarget)
            return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            browse();
        }
    }, [browse]);
    const rootProps = useMemo(() => {
        if (scopeEl)
            return { onKeyDown };
        return {
            onKeyDown,
            onDragEnter: dragEnter,
            onDragOver: dragOver,
            onDragLeave: dragLeave,
            onDrop: drop,
        };
    }, [scopeEl, onKeyDown, dragEnter, dragOver, dragLeave, drop]);
    const inputProps = {
        ref: inputRef,
        type: 'file',
        hidden: true,
        multiple: props.multiple ?? true,
        accept: (props.accept ?? ['image/', 'video/'])
            .map((entry) => (entry.endsWith('/') ? `${entry}*` : entry))
            .join(','),
        capture: props.capture,
        onChange: (e) => {
            addFiles(Array.from(e.target.files ?? []));
            e.target.value = '';
        },
    };
    const mode = modeOf(items.length);
    const doneCount = items.filter((i) => i.phase === 'done').length;
    const errorCount = items.filter((i) => i.phase === 'error').length;
    const allDone = items.length > 0 && doneCount === items.length;
    const settled = items.length > 0 && doneCount + errorCount === items.length;
    const totalBytes = items.reduce((n, i) => n + i.file.size, 0);
    const sentBytes = items.reduce((n, i) => n + i.file.size * (i.phase === 'done' ? 1 : i.progress), 0);
    const aggregate = totalBytes ? sentBytes / totalBytes : Number(allDone);
    const liveText = liveTextOf(labels, items, doneCount);
    return {
        uid,
        items,
        drag,
        notice,
        shake,
        mode,
        doneCount,
        errorCount,
        allDone,
        settled,
        aggregate,
        liveText,
        disabled: props.disabled ?? false,
        labels,
        addFiles,
        remove: session.remove,
        retry: session.retry,
        clear: session.clear,
        setDims: session.setDims,
        openPicker,
        browse,
        rootProps,
        inputProps,
        scopeEl,
    };
}
//# sourceMappingURL=useMediaDrop.js.map